import { AudioItem } from '../../items/audio/audio-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { GifItem } from '../../items/gif/gif-item-type';
import { EditorStarterItem } from '../../items/item-type';
import { VideoItem } from '../../items/video/video-item-type';
import { WebpItem } from '../../items/webp/webp-item-type';
import {
  AudioFadableItem,
  getCanFadeAudio,
  getCanFadeVisual,
  VisuallyFadableItem,
} from '../../utils/fade';
import { EditorState, TrackType } from '../types';
import { itemOwner, trimGroup } from '@waka/compat/group-split';
import { splitItem } from './split-item';
import {advanceNativeCallLaneBar} from '@waka/compat/call-lane-bar';
import {preserveItemAnimationWindow} from '@waka/compat/animation-window';

export const trimClipLeft = ({
  state,
  itemId,
  framePosition,
}: {
  state: EditorState;
  itemId: string;
  framePosition: number;
}): EditorState => {
  const targetItem: EditorStarterItem | undefined =
    state.undoableState.items[itemId];

  let targetTrack: TrackType | undefined;
  let targetTrackIndex = -1;

  for (let i = 0; i < state.undoableState.tracks.length; i++) {
    const track = state.undoableState.tracks[i];
    if (!track) continue;
    if (track.items.includes(itemId)) {
      targetTrack = track;
      targetTrackIndex = i;
      break;
    }
  }

  const owner = itemOwner(state, itemId);
  if (!targetTrack && owner) {
    targetTrack = owner.track;
    targetTrackIndex = state.undoableState.tracks.findIndex(
      (track) => track.id === owner.track.id,
    );
  }
  if (
    !targetTrack ||
    targetTrack.locked ||
    !targetItem ||
    targetTrackIndex === -1 ||
    !Number.isInteger(framePosition)
  ) {
    return state;
  }

  const itemStart = targetItem.from;
  const itemEnd = itemStart + targetItem.durationInFrames;

  if (framePosition <= itemStart || framePosition >= itemEnd) {
    return state;
  }
  if (targetItem.type === 'group')
    return trimGroup(state, itemId, framePosition, 'right', splitItem);

  const newDuration = itemEnd - framePosition;
  const trimmedDuration = framePosition - itemStart;
  const trimmedDurationInSeconds = trimmedDuration / state.undoableState.fps;

  const newItem: EditorStarterItem = {
    ...advanceNativeCallLaneBar(preserveItemAnimationWindow(targetItem,state.undoableState.fps,trimmedDuration),trimmedDuration),
    from: framePosition,
    durationInFrames: newDuration,
  };

  if (targetItem.type === 'video') {
    (newItem as VideoItem).videoStartFromInSeconds =
      (targetItem.videoStartFromInSeconds || 0) +
      trimmedDurationInSeconds * targetItem.playbackRate;
  }

  if (targetItem.type === 'audio') {
    (newItem as AudioItem).audioStartFromInSeconds =
      (targetItem.audioStartFromInSeconds || 0) +
      trimmedDurationInSeconds * targetItem.playbackRate;
  }

  if (targetItem.type === 'gif') {
    (newItem as GifItem).gifStartFromInSeconds =
      (targetItem.gifStartFromInSeconds || 0) +
      trimmedDurationInSeconds * targetItem.playbackRate;
  }

  if (targetItem.type === 'webp') {
    (newItem as WebpItem).webpStartFromInSeconds =
      (targetItem.webpStartFromInSeconds || 0) +
      trimmedDurationInSeconds * targetItem.playbackRate;
  }

  if (targetItem.type === 'captions') {
    (newItem as CaptionsItem).captionStartInSeconds =
      (targetItem.captionStartInSeconds || 0) + trimmedDurationInSeconds;
  }

  if (getCanFadeVisual(targetItem) && !newItem.visualFadeWindow) {
    (newItem as VisuallyFadableItem).fadeInDurationInSeconds = 0;
    (newItem as VisuallyFadableItem).fadeOutDurationInSeconds = Math.min(
      (newItem as VisuallyFadableItem).fadeOutDurationInSeconds,
      newDuration / state.undoableState.fps,
    );
  }

  if (getCanFadeAudio(targetItem) && !newItem.audioFadeWindow) {
    (newItem as AudioFadableItem).audioFadeInDurationInSeconds = 0;
    (newItem as AudioFadableItem).audioFadeOutDurationInSeconds = Math.min(
      (newItem as AudioFadableItem).audioFadeOutDurationInSeconds,
      newDuration / state.undoableState.fps,
    );
  }

  const newItems = {
    ...state.undoableState.items,
    [itemId]: newItem,
  };
  if ('positionKeyframes' in targetItem && 'positionKeyframes' in newItem)
    newItem.positionKeyframes = targetItem.positionKeyframes?.map((point) => ({
      ...point,
      frame: point.frame - trimmedDuration,
    }));
  if ('zoomKeyframes' in targetItem && 'zoomKeyframes' in newItem)
    newItem.zoomKeyframes = targetItem.zoomKeyframes?.map((point) => ({
      ...point,
      frame: point.frame - trimmedDuration,
    }));

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: newItems,
    },
    selectedItems: [itemId],
  };
};
